import { useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useLocation } from "wouter";
import DashboardSidebar from "./DashboardSidebar";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Menu, X } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

interface DashboardLayoutProps {
  children: React.ReactNode;
  pageTitle: string;
}

export default function DashboardLayout({ children, pageTitle }: DashboardLayoutProps) {
  const { user, logoutMutation } = useAuth();
  const [location] = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Please sign in</h1>
          <p className="text-slate-600 mb-4">You need to be logged in to access this page.</p>
          <Button asChild>
            <a href="/auth">Go to Login</a>
          </Button>
        </div>
      </div>
    );
  }

  const handleLogout = () => {
    logoutMutation.mutate();
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Mobile Header */}
      <header className="lg:hidden bg-white border-b border-slate-200 py-4 px-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-primary-600 flex items-center justify-center text-white font-bold">
                SA
              </div>
              <span className="text-lg font-bold text-primary-700">StaffAbility</span>
            </div>
          </div>

          <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0">
              <DashboardSidebar 
                collapsed={false}
                isMobile={true}
                closeMobileMenu={() => setSheetOpen(false)}
              />
            </SheetContent>
          </Sheet>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Desktop Sidebar */}
        <div className={`hidden lg:block ${collapsed ? 'w-16' : 'w-64'} transition-all duration-300 ease-in-out border-r border-slate-200 bg-white relative`}>
          <DashboardSidebar collapsed={collapsed} />
          <Button 
            variant="ghost" 
            size="icon" 
            className="absolute right-0 top-4 translate-x-1/2 z-10 bg-white border border-slate-200 rounded-full shadow-sm text-slate-500 hover:text-slate-700"
            onClick={() => setCollapsed(!collapsed)}
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </Button>
        </div>

        {/* Main Content */}
        <main className="flex-1 overflow-auto">
          <div className="p-4 md:p-6 lg:p-8">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6">
              <div>
                <h1 className="text-2xl font-bold text-slate-900">{pageTitle}</h1>
                <p className="text-slate-500">
                  {user.role === 'client' && 'Client Dashboard'}
                  {user.role === 'caseWorker' && 'Case Worker Dashboard'}
                  {user.role === 'admin' && 'Administrator Dashboard'}
                  {user.role === 'employer' && 'Employer Dashboard'}
                </p>
              </div>

              <div className="flex items-center mt-4 lg:mt-0 space-x-2">
                <div className="text-right mr-2 hidden sm:block">
                  <p className="text-sm font-medium text-slate-900">{user.fullName}</p>
                  <p className="text-xs text-slate-500">{user.email}</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center">
                  {user.fullName.split(' ').map(n => n[0]).join('').toUpperCase()}
                </div>
                <div className="sm:hidden">
                  <Button variant="ghost" size="sm" onClick={handleLogout}>
                    <X className="h-4 w-4 mr-1" />
                    Logout
                  </Button>
                </div>
              </div>
            </div>

            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
